import { colors } from "../tokens/colors";
import { hexToRgb } from "./colorUtils";

/**
 * Inject theme colors as CSS custom properties on the document root
 */
export const injectThemeColors = () => {
  const root = document.documentElement;

  // Primary colors
  Object.entries(colors.primary).forEach(([key, value]) => {
    const rgb = hexToRgb(value);
    root.style.setProperty(`--color-primary-${key}`, value);
    root.style.setProperty(`--color-primary-${key}-r`, rgb.r.toString());
    root.style.setProperty(`--color-primary-${key}-g`, rgb.g.toString());
    root.style.setProperty(`--color-primary-${key}-b`, rgb.b.toString());
  });

  // Secondary colors
  Object.entries(colors.secondary).forEach(([key, value]) => {
    const rgb = hexToRgb(value);
    root.style.setProperty(`--color-secondary-${key}`, value);
    root.style.setProperty(`--color-secondary-${key}-r`, rgb.r.toString());
    root.style.setProperty(`--color-secondary-${key}-g`, rgb.g.toString());
    root.style.setProperty(`--color-secondary-${key}-b`, rgb.b.toString());
  });

  // Light colors
  Object.entries(colors.light).forEach(([key, value]) => {
    const rgb = hexToRgb(value);
    root.style.setProperty(`--color-light-${key}`, value);
    root.style.setProperty(`--color-light-${key}-r`, rgb.r.toString());
    root.style.setProperty(`--color-light-${key}-g`, rgb.g.toString());
    root.style.setProperty(`--color-light-${key}-b`, rgb.b.toString());
  });

  // White color
  const whiteRgb = hexToRgb(colors.white);
  root.style.setProperty(`--color-white`, colors.white);
  root.style.setProperty(`--color-white-r`, whiteRgb.r.toString());
  root.style.setProperty(`--color-white-g`, whiteRgb.g.toString());
  root.style.setProperty(`--color-white-b`, whiteRgb.b.toString());

  // Status colors
  const statusColors = {
    success: colors.success,
    error: colors.error,
    warning: colors.warning,
    info: colors.info,
  };

  Object.entries(statusColors).forEach(([key, value]) => {
    const rgb = hexToRgb(value);
    root.style.setProperty(`--color-${key}`, value);
    root.style.setProperty(`--color-${key}-r`, rgb.r.toString());
    root.style.setProperty(`--color-${key}-g`, rgb.g.toString());
    root.style.setProperty(`--color-${key}-b`, rgb.b.toString());
  });

  // Static colors
  Object.entries(colors.static).forEach(([key, value]) => {
    const hex = value.length === 9 ? value.slice(0, 7) : value;
    const rgb = hexToRgb(hex);
    const legacyKey = value.replace("#", "").toLowerCase();

    // Legacy variables preserved for existing CSS usage.
    root.style.setProperty(`--color-static-${legacyKey}`, value);
    root.style.setProperty(`--color-static-${legacyKey}-r`, rgb.r.toString());
    root.style.setProperty(`--color-static-${legacyKey}-g`, rgb.g.toString());
    root.style.setProperty(`--color-static-${legacyKey}-b`, rgb.b.toString());

    // New variables based on semantic token keys.
    root.style.setProperty(`--color-static-${key}`, value);
    root.style.setProperty(`--color-static-${key}-r`, rgb.r.toString());
    root.style.setProperty(`--color-static-${key}-g`, rgb.g.toString());
    root.style.setProperty(`--color-static-${key}-b`, rgb.b.toString());
  });

  // About colors
  // Handle 8-digit hex colors (with alpha) by stripping alpha for RGB conversion
  const aboutSectionBgHex =
    colors.about.section.background.length === 9
      ? colors.about.section.background.slice(0, 7)
      : colors.about.section.background;
  const aboutSectionBgRgb = hexToRgb(aboutSectionBgHex);
  root.style.setProperty(`--color-about-section-background`, colors.about.section.background);
  root.style.setProperty(`--color-about-section-background-r`, aboutSectionBgRgb.r.toString());
  root.style.setProperty(`--color-about-section-background-g`, aboutSectionBgRgb.g.toString());
  root.style.setProperty(`--color-about-section-background-b`, aboutSectionBgRgb.b.toString());

  const aboutCardBgRgb = hexToRgb(colors.about.card.background);
  root.style.setProperty(`--color-about-card-background`, colors.about.card.background);
  root.style.setProperty(`--color-about-card-background-r`, aboutCardBgRgb.r.toString());
  root.style.setProperty(`--color-about-card-background-g`, aboutCardBgRgb.g.toString());
  root.style.setProperty(`--color-about-card-background-b`, aboutCardBgRgb.b.toString());

  const aboutCardBorderRgb = hexToRgb(colors.about.card.border);
  root.style.setProperty(`--color-about-card-border`, colors.about.card.border);
  root.style.setProperty(`--color-about-card-border-r`, aboutCardBorderRgb.r.toString());
  root.style.setProperty(`--color-about-card-border-g`, aboutCardBorderRgb.g.toString());
  root.style.setProperty(`--color-about-card-border-b`, aboutCardBorderRgb.b.toString());

  const aboutCardTextRgb = hexToRgb(colors.about.card.text);
  root.style.setProperty(`--color-about-card-text`, colors.about.card.text);
  root.style.setProperty(`--color-about-card-text-r`, aboutCardTextRgb.r.toString());
  root.style.setProperty(`--color-about-card-text-g`, aboutCardTextRgb.g.toString());
  root.style.setProperty(`--color-about-card-text-b`, aboutCardTextRgb.b.toString());

  // Pricing colors
  // Handle 8-digit hex colors (with alpha) by stripping alpha for RGB conversion
  const pricingSectionBgHex =
    colors.pricing.section.background.length === 9
      ? colors.pricing.section.background.slice(0, 7)
      : colors.pricing.section.background;
  const pricingSectionBgRgb = hexToRgb(pricingSectionBgHex);
  root.style.setProperty(`--color-pricing-section-background`, colors.pricing.section.background);
  root.style.setProperty(`--color-pricing-section-background-r`, pricingSectionBgRgb.r.toString());
  root.style.setProperty(`--color-pricing-section-background-g`, pricingSectionBgRgb.g.toString());
  root.style.setProperty(`--color-pricing-section-background-b`, pricingSectionBgRgb.b.toString());

  const pricingSavingsCardBgHex =
    colors.pricing.savingsCard.background.length === 9
      ? colors.pricing.savingsCard.background.slice(0, 7)
      : colors.pricing.savingsCard.background;
  const pricingSavingsCardBgRgb = hexToRgb(pricingSavingsCardBgHex);
  root.style.setProperty(
    `--color-pricing-savings-card-background`,
    colors.pricing.savingsCard.background
  );
  root.style.setProperty(
    `--color-pricing-savings-card-background-r`,
    pricingSavingsCardBgRgb.r.toString()
  );
  root.style.setProperty(
    `--color-pricing-savings-card-background-g`,
    pricingSavingsCardBgRgb.g.toString()
  );
  root.style.setProperty(
    `--color-pricing-savings-card-background-b`,
    pricingSavingsCardBgRgb.b.toString()
  );

  const pricingCardBgHex =
    colors.pricing.pricingCard.background.length === 9
      ? colors.pricing.pricingCard.background.slice(0, 7)
      : colors.pricing.pricingCard.background;
  const pricingCardBgRgb = hexToRgb(pricingCardBgHex);
  root.style.setProperty(`--color-pricing-card-background`, colors.pricing.pricingCard.background);
  root.style.setProperty(`--color-pricing-card-background-r`, pricingCardBgRgb.r.toString());
  root.style.setProperty(`--color-pricing-card-background-g`, pricingCardBgRgb.g.toString());
  root.style.setProperty(`--color-pricing-card-background-b`, pricingCardBgRgb.b.toString());
};
